import { useEffect } from "react";

const TAILWIND_SRC = "https://cdn.tailwindcss.com";

/**
 * Loads Tailwind CDN only while this hook is mounted.
 * Defaults to disabling preflight to avoid global resets.
 */
export function useTailwindCdn({
  corePlugins = { preflight: false },
  theme,
  plugins,
} = {}) {
  useEffect(() => {
    // refcount so multiple production screens can share one load
    window.__tw_refcount = (window.__tw_refcount || 0) + 1;

    // configure BEFORE loading cdn
    window.tailwind = window.tailwind || {};
    window.tailwind.config = { corePlugins, theme, plugins };

    // already loaded?
    let script = document.querySelector(`script[src="${TAILWIND_SRC}"]`);
    let observer = null;

    // tag any generated <style> so we can remove it later
    const tagTailwindStyles = () => {
      document.querySelectorAll("style").forEach((s) => {
        const t = s.textContent || "";
        if (t.includes("! tailwindcss")) s.dataset.ephemeral = "tailwind-style";
      });
    };

    if (!script) {
      observer = new MutationObserver(tagTailwindStyles);
      observer.observe(document.head, { childList: true, subtree: true });

      script = document.createElement("script");
      script.src = TAILWIND_SRC;
      script.async = true;
      script.dataset.ephemeral = "tailwind-script";
      script.onload = tagTailwindStyles;
      document.head.appendChild(script);
    } else {
      // script exists (navigating between production subroutes)
      tagTailwindStyles();
    }

    return () => {
      window.__tw_refcount -= 1;
      const stillNeeded = window.__tw_refcount > 0;

      if (!stillNeeded) {
        // remove script
        const s = document.querySelector(
          `script[src="${TAILWIND_SRC}"][data-ephemeral="tailwind-script"]`
        );
        s?.parentNode?.removeChild(s);

        // remove tailwind-generated <style> tags
        document
          .querySelectorAll('style[data-ephemeral="tailwind-style"]')
          .forEach((el) => el.parentNode?.removeChild(el));

        try { delete window.tailwind; } catch {}
      }

      observer?.disconnect?.();
    };
  }, [JSON.stringify(corePlugins), JSON.stringify(theme), JSON.stringify(plugins)]);
}
